/**
 * Client-side Validation Service
 * Provides comprehensive validation for all form inputs before API calls
 */

import { CSVFileEntry, TimeInformationDatabaseData, ZeitschritteData } from '../../../types/common';
import { ModelParameters } from '../../../features/training/components/ModelConfiguration';
import { TrainingDataSplitData } from '../../../features/training/components/TrainingDataSplit';
import { validateModelParameters } from '../../../utils/modelParameterUtils';
import { DEFAULT_MAX_FILE_SIZE_MB } from '../../../hooks/useFileSizeLimit';

export interface FormValidationResult {
  isValid: boolean;
  errors: FormValidationError[];
  warnings: FormValidationWarning[];
}

export interface FormValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface FormValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

export class ValidationService {
  /**
   * Validate CSV files
   * @param inputFiles - Input CSV files to validate
   * @param outputFiles - Output CSV files to validate
   * @param maxFileSizeMB - Maximum file size in MB (defaults to subscription plan limit)
   */
  static validateCSVFiles(
    inputFiles: CSVFileEntry[], 
    outputFiles: CSVFileEntry[],
    maxFileSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB
  ): FormValidationResult {
    const result: FormValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check if at least one file is provided
    const totalFiles = inputFiles.filter(f => f.file !== null).length + 
                      outputFiles.filter(f => f.file !== null).length;
    
    if (totalFiles === 0) {
      result.errors.push({
        field: 'files',
        message: 'At least one CSV file is required',
        severity: 'error'
      });
      result.isValid = false;
    }

    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    // Validate input files
    inputFiles.forEach((file, index) => {
      if (file.file) {
        // Check file size (dynamic limit based on subscription plan)
        if (file.file.size > maxFileSizeBytes) {
          result.warnings.push({
            field: `inputFile${index}`,
            message: `File ${file.fileName} is large (${Math.round(file.file.size / 1024 / 1024)}MB). Upload may take time.`,
            suggestion: `Consider splitting large files. Maximum allowed: ${maxFileSizeMB}MB`
          });
        }

        // Check file extension
        if (!file.fileName.toLowerCase().endsWith('.csv')) {
          result.errors.push({
            field: `inputFile${index}`,
            message: `File ${file.fileName} is not a CSV file`,
            severity: 'error'
          });
          result.isValid = false;
        }
      }
    });

    // Validate output files
    outputFiles.forEach((file, index) => {
      if (file.file) {
        // Check file size (dynamic limit based on subscription plan)
        if (file.file.size > maxFileSizeBytes) {
          result.warnings.push({
            field: `outputFile${index}`,
            message: `File ${file.fileName} is large (${Math.round(file.file.size / 1024 / 1024)}MB). Upload may take time.`,
            suggestion: `Consider splitting large files. Maximum allowed: ${maxFileSizeMB}MB`
          });
        }

        // Check file extension
        if (!file.fileName.toLowerCase().endsWith('.csv')) {
          result.errors.push({
            field: `outputFile${index}`,
            message: `File ${file.fileName} is not a CSV file`,
            severity: 'error'
          });
          result.isValid = false;
        }
      }
    });

    return result;
  }

  /**
   * Validate time information
   */
  static validateTimeInformation(timeInfo: TimeInformationDatabaseData): FormValidationResult {
    const result: FormValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check if at least one time feature is selected
    const timeFeatures = [timeInfo.jahr, timeInfo.woche, timeInfo.monat, timeInfo.feiertag, timeInfo.tag];
    const hasAnyTimeFeature = timeFeatures.some(feature => feature === true);

    if (!hasAnyTimeFeature) {
      result.warnings.push({
        field: 'timeFeatures',
        message: 'No time features selected. This may limit model performance.',
        suggestion: 'Consider enabling at least one time feature for better predictions'
      });
    }

    // Validate timezone if time features are enabled
    if (hasAnyTimeFeature && (!timeInfo.zeitzone || timeInfo.zeitzone.trim() === '')) {
      result.errors.push({
        field: 'zeitzone',
        message: 'Timezone is required when time features are enabled',
        severity: 'error'
      });
      result.isValid = false;
    }

    // Validate timezone format
    if (timeInfo.zeitzone && timeInfo.zeitzone.trim() !== '') {
      const validTimezonePattern = /^[A-Za-z]+\/[A-Za-z_]+$/;
      if (!validTimezonePattern.test(timeInfo.zeitzone)) {
        result.warnings.push({
          field: 'zeitzone',
          message: 'Timezone format may be invalid',
          suggestion: 'Use format like "Europe/Vienna" or "America/New_York"'
        });
      }
    }

    return result;
  }

  /**
   * Validate zeitschritte data
   */
  static validateZeitschritte(zeitschritte: ZeitschritteData): FormValidationResult {
    const result: FormValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Validate eingabe (input time steps)
    if (!zeitschritte.eingabe || zeitschritte.eingabe.trim() === '') {
      result.errors.push({
        field: 'eingabe',
        message: 'Input time steps is required',
        severity: 'error'
      });
      result.isValid = false;
    } else {
      const eingabeNum = parseInt(zeitschritte.eingabe);
      if (isNaN(eingabeNum) || eingabeNum <= 0) {
        result.errors.push({
          field: 'eingabe',
          message: 'Input time steps must be a positive number',
          severity: 'error'
        });
        result.isValid = false;
      } else if (eingabeNum > 1000) {
        result.warnings.push({
          field: 'eingabe',
          message: 'Very large input time steps may impact performance',
          suggestion: 'Consider using fewer time steps for faster training'
        });
      }
    }

    // Validate ausgabe (output time steps)
    if (!zeitschritte.ausgabe || zeitschritte.ausgabe.trim() === '') {
      result.errors.push({
        field: 'ausgabe',
        message: 'Output time steps is required',
        severity: 'error'
      });
      result.isValid = false;
    } else {
      const ausgabeNum = parseInt(zeitschritte.ausgabe);
      if (isNaN(ausgabeNum) || ausgabeNum <= 0) {
        result.errors.push({
          field: 'ausgabe',
          message: 'Output time steps must be a positive number',
          severity: 'error'
        });
        result.isValid = false;
      }
    }

    // Validate zeitschrittweite (time step width)
    if (!zeitschritte.zeitschrittweite || zeitschritte.zeitschrittweite.trim() === '') {
      result.errors.push({
        field: 'zeitschrittweite',
        message: 'Time step width is required',
        severity: 'error'
      });
      result.isValid = false;
    } else {
      const zeitschrittweiteNum = parseFloat(zeitschritte.zeitschrittweite);
      if (isNaN(zeitschrittweiteNum) || zeitschrittweiteNum <= 0) {
        result.errors.push({
          field: 'zeitschrittweite',
          message: 'Time step width must be a positive number',
          severity: 'error'
        });
        result.isValid = false;
      }
    }

    // Validate offset
    if (zeitschritte.offset && zeitschritte.offset.trim() !== '') {
      const offsetNum = parseFloat(zeitschritte.offset);
      if (isNaN(offsetNum)) {
        result.errors.push({
          field: 'offset',
          message: 'Offset must be a valid number',
          severity: 'error'
        });
        result.isValid = false;
      }
    }

    return result;
  }

  /**
   * Validate model configuration
   */
  static validateModelConfiguration(modelConfig: ModelParameters): FormValidationResult {
    const result: FormValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    const validation = validateModelParameters(modelConfig);
    
    result.isValid = validation.isValid;
    
    // Convert ValidationResult to FormValidationResult
    validation.errors.forEach((error: string) => {
      result.errors.push({
        field: 'modelConfiguration',
        message: error,
        severity: 'error'
      });
    });

    validation.warnings.forEach((warning: string) => {
      result.warnings.push({
        field: 'modelConfiguration',
        message: warning,
        suggestion: 'Parameters will be automatically corrected'
      });
    });

    return result;
  }

  /**
   * Validate training data split
   */
  static validateTrainingDataSplit(split: TrainingDataSplitData, totalDataPoints: number): FormValidationResult {
    const result: FormValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check if percentages add up to 100
    const totalPercentage = split.trainPercentage + split.valPercentage + split.testPercentage;
    if (Math.abs(totalPercentage - 100) > 0.01) {
      result.errors.push({
        field: 'percentages',
        message: `Training split percentages must add up to 100%. Current total: ${totalPercentage.toFixed(1)}%`,
        severity: 'error'
      });
      result.isValid = false;
    }

    // Validate individual percentages
    if (split.trainPercentage < 50) {
      result.warnings.push({
        field: 'trainPercentage',
        message: 'Training percentage is very low. This may affect model performance.',
        suggestion: 'Consider using at least 60% for training'
      });
    }

    if (split.valPercentage < 10) {
      result.warnings.push({
        field: 'valPercentage',
        message: 'Validation percentage is very low.',
        suggestion: 'Consider using at least 15% for validation'
      });
    }

    if (split.testPercentage < 10) {
      result.warnings.push({
        field: 'testPercentage',
        message: 'Test percentage is very low.',
        suggestion: 'Consider using at least 15% for testing'
      });
    }

    // Check absolute numbers if total data points is known
    if (totalDataPoints > 0) {
      const trainSize = Math.floor(totalDataPoints * split.trainPercentage / 100);
      const valSize = Math.floor(totalDataPoints * split.valPercentage / 100);
      const testSize = Math.floor(totalDataPoints * split.testPercentage / 100);

      if (trainSize < 10) {
        result.warnings.push({
          field: 'trainSize',
          message: `Training set will be very small (${trainSize} samples)`,
          suggestion: 'Consider increasing training percentage or using more data'
        });
      }

      if (valSize < 5) {
        result.warnings.push({
          field: 'valSize',
          message: `Validation set will be very small (${valSize} samples)`,
          suggestion: 'Consider increasing validation percentage'
        });
      }

      if (testSize < 5) {
        result.warnings.push({
          field: 'testSize',
          message: `Test set will be very small (${testSize} samples)`,
          suggestion: 'Consider increasing test percentage'
        });
      }
    }

    return result;
  }

  /**
   * Validate complete form before submission
   */
  static validateCompleteForm(data: {
    inputFiles: CSVFileEntry[];
    outputFiles: CSVFileEntry[];
    timeInfo: TimeInformationDatabaseData;
    zeitschritte: ZeitschritteData;
    modelConfiguration: ModelParameters;
    trainingDataSplit: TrainingDataSplitData;
    totalDataPoints: number;
  }): FormValidationResult {
    const results: FormValidationResult[] = [
      this.validateCSVFiles(data.inputFiles, data.outputFiles),
      this.validateTimeInformation(data.timeInfo),
      this.validateZeitschritte(data.zeitschritte),
      this.validateModelConfiguration(data.modelConfiguration),
      this.validateTrainingDataSplit(data.trainingDataSplit, data.totalDataPoints)
    ];

    // Combine all results
    const combinedResult: FormValidationResult = {
      isValid: results.every(r => r.isValid),
      errors: results.flatMap(r => r.errors),
      warnings: results.flatMap(r => r.warnings)
    };

    return combinedResult;
  }

  /**
   * Get validation summary for display
   */
  static getValidationSummary(result: FormValidationResult): string {
    if (result.isValid && result.warnings.length === 0) {
      return 'All validations passed ✅';
    }

    const errorCount = result.errors.length;
    const warningCount = result.warnings.length;

    let summary = '';
    if (errorCount > 0) {
      summary += `${errorCount} error${errorCount > 1 ? 's' : ''} ❌`;
    }
    if (warningCount > 0) {
      if (summary) summary += ', ';
      summary += `${warningCount} warning${warningCount > 1 ? 's' : ''} ⚠️`;
    }

    return summary;
  }
}